import { Checkbox, CircularProgress } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import classNames from "classnames";
import * as React from "react";

import { ErrorBoundary } from "@/shared/components/ErrorBoundary";

import { type FilterOption, type FilterOptions } from "../FilterDefinition";
import {
  type EnumComparator,
  type SetComparator,
  isValueless,
} from "../FilterSchema";

import styles from "./OptionsValueInput.module.css";

interface OptionsValueInputProps {
  comparator: SetComparator | EnumComparator;
  value: string[];
  onChange: (next: string[]) => void;
  options: FilterOptions;
}

interface NormalizedOption {
  label: string;
  value: string;
}

function normalizeOption(option: FilterOption): NormalizedOption {
  return typeof option === "string" ? { label: option, value: option } : option;
}

/**
 * Searchable, multi-select checkbox list shared by the ``set`` and ``enum``
 * filter types. Valueless comparators (``IS_NULL`` / ``IS_NOT_NULL``) render
 * nothing — redundant with ``FilterForm``, which already hides the value row
 * for them.
 *
 * ``options`` is ``string[]`` (wire value === label) or
 * ``LabeledOption[]`` (distinct ``value`` and ``label``, for opaque ids),
 * supplied directly or returned from an async resolver. The async path runs
 * through ``useSuspenseQuery`` for loading/error ergonomics only; its query
 * key is scoped per instance (``React.useId``), so caching is the consumer's
 * job (e.g. ``queryClient.ensureQueryData(...)``).
 *
 * The list is filtered client-side with no pagination, so consumers must keep
 * the option set small enough to hold in memory.
 */
export function OptionsValueInput({
  comparator,
  value,
  onChange,
  options,
}: OptionsValueInputProps) {
  const [searchValue, setSearchValue] = React.useState("");
  const instanceId = React.useId();

  if (isValueless(comparator)) {
    return null;
  }

  // TODO Use an MUI text input for search.
  // Or a tags autocomplete instead.
  return (
    <div className={styles.input}>
      <input
        type="search"
        className={styles.searchInput}
        placeholder="Search options..."
        aria-label="Search options"
        value={searchValue}
        onChange={(event) => setSearchValue(event.target.value)}
      />
      <ErrorBoundary fallback={renderErrorState}>
        <React.Suspense fallback={<LoadingState />}>
          <OptionList
            instanceId={instanceId}
            options={options}
            searchValue={searchValue}
            value={value}
            onChange={onChange}
          />
        </React.Suspense>
      </ErrorBoundary>
    </div>
  );
}

interface OptionListProps {
  instanceId: string;
  options: FilterOptions;
  searchValue: string;
  value: string[];
  onChange: (next: string[]) => void;
}

function OptionList({
  instanceId,
  options,
  searchValue,
  value,
  onChange,
}: OptionListProps) {
  const isLazy = typeof options === "function";

  // The static path supplies `initialData`, so Suspense never activates
  // for it. The lazy path has no initial data and suspends until the
  // promise resolves. Either way, `data` is the resolved homogeneous
  // option list — `string[]` or `LabeledOption[]` per the no-mixing
  // guarantee. Widen to the element union so the normalize pass below
  // runs over a single array type rather than a union of arrays.
  const { data } = useSuspenseQuery({
    queryKey: [instanceId],
    queryFn: isLazy ? options : () => Promise.resolve(options),
    initialData: isLazy ? undefined : options,
  });
  const resolved: FilterOption[] = data;

  // Normalize once per ``data`` change so the search and render passes
  // both operate on ``{ label, value }`` shape regardless of whether the
  // caller supplied plain strings or labeled objects. Search matches
  // ``label`` — what the user sees — not the opaque wire ``value``.
  const normalized = React.useMemo(
    () => resolved.map(normalizeOption),
    [resolved],
  );
  const filteredOptions = React.useMemo(() => {
    const needle = searchValue.trim().toLowerCase();
    if (needle === "") {
      return normalized;
    }
    return normalized.filter((option) =>
      option.label.toLowerCase().includes(needle),
    );
  }, [normalized, searchValue]);

  if (filteredOptions.length === 0) {
    return (
      <div className={styles.status} role="status" aria-live="polite">
        No matching options
      </div>
    );
  }

  const toggleOption = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  return (
    <ul
      className={styles.list}
      role="listbox"
      aria-label="Filterable values"
      aria-multiselectable="true"
    >
      {filteredOptions.map((option) => {
        const isSelected = value.includes(option.value);
        return (
          <li key={option.value} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={isSelected}
              className={styles.row}
              onClick={() => toggleOption(option.value)}
            >
              <Checkbox
                size="small"
                checked={isSelected}
                tabIndex={-1}
                disableRipple
                className={styles.checkbox}
              />
              <span className={styles.rowLabel}>{option.label}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function LoadingState() {
  return (
    <div className={styles.status} role="status" aria-live="polite">
      <CircularProgress size="1rem" />
      <span>Loading options…</span>
    </div>
  );
}

function renderErrorState() {
  return (
    <div className={classNames(styles.status, styles.error)} role="alert">
      Couldn’t load options. Reopen the menu to retry.
    </div>
  );
}
